import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import { 
  TrendingUp, TrendingDown, Users, Target, DollarSign, 
  CheckCircle, FileText, Clock, Award, BarChart3, PieChart,
  ChevronUp, ChevronDown, Minus, Package, Layers, RefreshCw
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import VideoHelpButton from '../components/VideoHelpButton';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default empty data structures
const defaultLeaderboardData = [];
const defaultTrendData = { labels: [], leads: [], proposals: [], won: [], revenue: [] };
const defaultLossReasons = [];
const defaultIndustries = [];
const defaultProductData = [];
const defaultBundledProducts = [];

// Sort Icon Component - defined outside
const SortIcon = ({ sortBy, sortOrder, column }) => {
  if (sortBy !== column) return <Minus className="w-3 h-3 opacity-30" />;
  return sortOrder === 'desc' ? 
    <ChevronDown className="w-3 h-3 text-[#A2FF00]" /> : 
    <ChevronUp className="w-3 h-3 text-[#A2FF00]" />;
};

// Rank Badge Component
const RankBadge = ({ index, isDark }) => {
  if (index === 0) return <span className="text-lg">🥇</span>;
  if (index === 1) return <span className="text-lg">🥈</span>;
  if (index === 2) return <span className="text-lg">🥉</span>;
  return (
    <span className={`w-6 h-6 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} flex items-center justify-center text-xs font-bold`}>
      {index + 1}
    </span>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, comparison, suffix = '', prefix = '', isDark }) => {
  const isPositive = comparison > 0;
  const isNeutral = comparison === 0;
  
  return (
    <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
      <div className="flex items-start justify-between mb-2">
        <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{title}</span>
        <Icon className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-500'}`} />
      </div>
      <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </div>
      <div className={`flex items-center gap-1 mt-2 text-xs ${
        isNeutral ? (isDark ? 'text-gray-500' : 'text-gray-500') :
        isPositive ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')
      }`}>
        {isNeutral ? (
          <Minus className="w-3 h-3" />
        ) : isPositive ? (
          <TrendingUp className="w-3 h-3" />
        ) : (
          <TrendingDown className="w-3 h-3" />
        )}
        <span>{isPositive ? '+' : ''}{comparison}% vs team avg</span>
      </div>
    </div>
  );
};

// Simple Bar Chart Component
const SimpleBarChart = ({ data, maxValue, color = '#A2FF00', isDark }) => {
  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-700'} w-24 truncate`}>{item.label}</span>
          <div className={`flex-1 h-6 ${isDark ? 'bg-white/5' : 'bg-gray-200'} rounded-sm overflow-hidden`}>
            <div 
              className="h-full rounded-sm transition-all duration-500"
              style={{ 
                width: `${(item.value / maxValue) * 100}%`,
                backgroundColor: isDark ? color : '#16a34a'
              }}
            />
          </div>
          <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'} w-16 text-right`}>
            {item.displayValue || item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// Simple Trend Chart Component
const SimpleTrendChart = ({ data, labels, isDark }) => {
  const maxVal = Math.max(...data);
  const minVal = Math.min(...data);
  const range = maxVal - minVal || 1;
  
  return (
    <div className="relative h-40">
      <div className="absolute inset-0 flex flex-col justify-between">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className={`border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`} />
        ))}
      </div>
      
      <div className="absolute inset-0 flex items-end justify-between px-2">
        {data.map((value, index) => {
          const height = ((value - minVal) / range) * 100;
          return (
            <div key={index} className="flex flex-col items-center gap-1" style={{ height: '100%' }}>
              <div className="flex-1 flex items-end">
                <div 
                  className={`w-3 ${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} rounded-t-sm transition-all duration-500`}
                  style={{ height: `${Math.max(height, 5)}%` }}
                />
              </div>
              <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{labels[index]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Donut Chart Component
const DonutChart = ({ data, isDark }) => {
  const total = data.reduce((sum, item) => sum + item.count, 0);
  const colors = ['#EF4444', '#F59E0B', '#3B82F6', '#8B5CF6', '#6B7280'];
  
  // Pre-calculate all angles
  const segments = data.map((item, index) => {
    const prevItems = data.slice(0, index);
    const startAngle = prevItems.reduce((sum, prev) => sum + (prev.count / total) * 360, 0);
    const angle = (item.count / total) * 360;
    return { ...item, startAngle, angle, color: colors[index] };
  });
  
  return (
    <div className="flex items-center gap-6">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 100 100" className="transform -rotate-90">
          {segments.map((segment, index) => {
            const x1 = 50 + 40 * Math.cos((segment.startAngle * Math.PI) / 180);
            const y1 = 50 + 40 * Math.sin((segment.startAngle * Math.PI) / 180);
            const x2 = 50 + 40 * Math.cos(((segment.startAngle + segment.angle) * Math.PI) / 180);
            const y2 = 50 + 40 * Math.sin(((segment.startAngle + segment.angle) * Math.PI) / 180);
            const largeArc = segment.angle > 180 ? 1 : 0;
            
            return (
              <path
                key={index}
                d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                fill={segment.color}
                className="hover:opacity-80 transition-opacity cursor-pointer"
              />
            );
          })}
          <circle cx="50" cy="50" r="25" fill={isDark ? '#0A0A0A' : '#ffffff'} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{total}</span>
        </div>
      </div>
      
      <div className="space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: colors[index] }} />
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.reason}</span>
            <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const IntelligencePage = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [timePeriod, setTimePeriod] = useState('month');
  const [viewMode, setViewMode] = useState('team');
  const [sortBy, setSortBy] = useState('revenue');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [leaderboardData, setLeaderboardData] = useState(defaultLeaderboardData);
  const [myStats, setMyStats] = useState(null);
  const [trendData, setTrendData] = useState(defaultTrendData);
  const [lossReasons, setLossReasons] = useState(defaultLossReasons);
  const [industries, setIndustries] = useState(defaultIndustries);
  const [productData, setProductData] = useState(defaultProductData);
  const [bundledProducts, setBundledProducts] = useState(defaultBundledProducts);
  const [teamAverages, setTeamAverages] = useState(null);

  const currentUserId = user?.user_id;

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const fetchOptions = {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    };

    // Helper function to safely fetch and parse JSON
    const safeFetch = async (url, fallback = null) => {
      try {
        const res = await fetch(url, fetchOptions);
        if (res.ok) {
          return await res.json();
        }
        console.warn(`API returned ${res.status} for ${url}`);
        return fallback;
      } catch (err) {
        console.error(`Failed to fetch ${url}:`, err);
        return fallback;
      }
    };

    try {
      // Fetch all endpoints in parallel with individual error handling
      const [
        leaderboardData,
        myStatsData,
        trendsData,
        lossReasonsData,
        industriesData,
        productsData,
        bundlesData,
        teamAvgData
      ] = await Promise.all([
        safeFetch(`${BACKEND_URL}/api/intelligence/leaderboard?period=${timePeriod}`, []),
        safeFetch(`${BACKEND_URL}/api/intelligence/my-stats?period=${timePeriod}`, null),
        safeFetch(`${BACKEND_URL}/api/intelligence/trends?period=${timePeriod}`, { dates: [], revenue: [], proposals: [] }),
        safeFetch(`${BACKEND_URL}/api/intelligence/loss-reasons?period=${timePeriod}`, []),
        safeFetch(`${BACKEND_URL}/api/intelligence/industries?period=${timePeriod}`, []),
        safeFetch(`${BACKEND_URL}/api/intelligence/products?period=${timePeriod}`, []),
        safeFetch(`${BACKEND_URL}/api/intelligence/bundles?period=${timePeriod}`, []),
        safeFetch(`${BACKEND_URL}/api/intelligence/team-averages?period=${timePeriod}`, null)
      ]);

      // Update state with fetched data
      if (leaderboardData) setLeaderboardData(leaderboardData);
      if (myStatsData) setMyStats(myStatsData);
      if (trendsData) setTrendData(trendsData);
      if (lossReasonsData) setLossReasons(lossReasonsData);
      if (industriesData) setIndustries(industriesData);
      if (productsData) setProductData(productsData);
      if (bundlesData) setBundledProducts(bundlesData);
      if (teamAvgData) setTeamAverages(teamAvgData);
    } catch (err) {
      console.error('Failed to fetch intelligence data:', err);
      setError('Failed to load analytics data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedLeaderboard = [...leaderboardData].sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];
    return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const currentUserStats = leaderboardData.find(u => u.user_id === currentUserId) || myStats || leaderboardData[0];
  
  // Use fetched team averages or calculate from leaderboard
  const calculatedTeamAverages = teamAverages || (leaderboardData.length > 0 ? {
    leads: Math.round(leaderboardData.reduce((sum, u) => sum + u.leads, 0) / leaderboardData.length),
    tasks: Math.round(leaderboardData.reduce((sum, u) => sum + u.tasks, 0) / leaderboardData.length),
    proposals: Math.round(leaderboardData.reduce((sum, u) => sum + u.proposals, 0) / leaderboardData.length),
    won: Math.round(leaderboardData.reduce((sum, u) => sum + u.won, 0) / leaderboardData.length),
    revenue: Math.round(leaderboardData.reduce((sum, u) => sum + u.revenue, 0) / leaderboardData.length),
    close_rate: Math.round(leaderboardData.reduce((sum, u) => sum + u.close_rate, 0) / leaderboardData.length * 10) / 10,
    avg_deal: Math.round(leaderboardData.reduce((sum, u) => sum + u.avg_deal, 0) / leaderboardData.length),
    time_to_close: Math.round(leaderboardData.reduce((sum, u) => sum + u.time_to_close, 0) / leaderboardData.length)
  } : { leads: 0, tasks: 0, proposals: 0, won: 0, revenue: 0, close_rate: 0, avg_deal: 0, time_to_close: 0 });

  const pipelineValue = myStats?.pipeline_value || 0;
  const conversionRate = myStats?.close_rate || calculatedTeamAverages?.close_rate || 0;

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Show loading state
  if (loading && leaderboardData.length === 0) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 animate-spin text-[#A2FF00]" />
            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading analytics...</span>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Intelligence
            </h1>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
              Performance analytics and insights
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              disabled={loading}
              className={`p-2 rounded-sm transition-colors ${
                isDark ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
              } ${loading ? 'opacity-50' : ''}`}
              title="Refresh data"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            
            <div className={`flex items-center p-1 rounded-sm ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              {['week', 'month', 'year', 'all'].map((period) => (
                <button
                  key={period}
                  onClick={() => setTimePeriod(period)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                    timePeriod === period
                      ? 'bg-[#A2FF00] text-black'
                      : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {period === 'week' ? 'This Week' : period === 'month' ? 'This Month' : period === 'year' ? 'This Year' : 'All Time'}
                </button>
              ))}
            </div>
            
            <div className={`flex items-center p-1 rounded-sm ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <button
                onClick={() => setViewMode('my')}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                  viewMode === 'my'
                    ? 'bg-[#A2FF00] text-black'
                    : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                My Stats
              </button>
              <button
                onClick={() => setViewMode('team')}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                  viewMode === 'team'
                    ? 'bg-[#A2FF00] text-black'
                    : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Team Leaderboard
              </button>
            </div>
          </div>
        </div>
        
        {/* Error message */}
        {error && (
          <div className={`p-4 rounded-sm ${isDark ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
            <p className={isDark ? 'text-red-400' : 'text-red-700'}>{error}</p>
          </div>
        )}

        {/* Section 1: League Table */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm overflow-hidden`}>
          <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center gap-2`}>
            <Award className="w-5 h-5 text-[#A2FF00]" />
            <h2 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Performance Leaderboard</h2>
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'} ml-2`}>
              {timePeriod === 'week' ? 'This Week' : timePeriod === 'month' ? 'This Month' : timePeriod === 'year' ? 'This Year' : 'All Time'}
            </span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} text-xs`}>
                  <th className={`px-4 py-3 text-left ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Rank</th>
                  <th className={`px-4 py-3 text-left ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Team Member</th>
                  <th className={`px-4 py-3 text-right cursor-pointer ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-700 hover:text-green-600'}`} onClick={() => handleSort('leads')}>
                    <div className="flex items-center justify-end gap-1">Leads <SortIcon sortBy={sortBy} sortOrder={sortOrder} column="leads" /></div>
                  </th>
                  <th className={`px-4 py-3 text-right cursor-pointer ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-700 hover:text-green-600'}`} onClick={() => handleSort('tasks')}>
                    <div className="flex items-center justify-end gap-1">Tasks <SortIcon sortBy={sortBy} sortOrder={sortOrder} column="tasks" /></div>
                  </th>
                  <th className={`px-4 py-3 text-right cursor-pointer ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-700 hover:text-green-600'}`} onClick={() => handleSort('proposals')}>
                    <div className="flex items-center justify-end gap-1">Proposals <SortIcon sortBy={sortBy} sortOrder={sortOrder} column="proposals" /></div>
                  </th>
                  <th className={`px-4 py-3 text-right cursor-pointer ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-700 hover:text-green-600'}`} onClick={() => handleSort('won')}>
                    <div className="flex items-center justify-end gap-1">Won <SortIcon sortBy={sortBy} sortOrder={sortOrder} column="won" /></div>
                  </th>
                  <th className={`px-4 py-3 text-right cursor-pointer ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-700 hover:text-green-600'}`} onClick={() => handleSort('revenue')}>
                    <div className="flex items-center justify-end gap-1">Revenue <SortIcon sortBy={sortBy} sortOrder={sortOrder} column="revenue" /></div>
                  </th>
                  <th className={`px-4 py-3 text-right cursor-pointer ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-700 hover:text-green-600'}`} onClick={() => handleSort('close_rate')}>
                    <div className="flex items-center justify-end gap-1">Close % <SortIcon sortBy={sortBy} sortOrder={sortOrder} column="close_rate" /></div>
                  </th>
                  <th className={`px-4 py-3 text-right cursor-pointer ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-700 hover:text-green-600'}`} onClick={() => handleSort('avg_deal')}>
                    <div className="flex items-center justify-end gap-1">Avg Deal <SortIcon sortBy={sortBy} sortOrder={sortOrder} column="avg_deal" /></div>
                  </th>
                  <th className={`px-4 py-3 text-right cursor-pointer ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-700 hover:text-green-600'}`} onClick={() => handleSort('time_to_close')}>
                    <div className="flex items-center justify-end gap-1">Days to Close <SortIcon sortBy={sortBy} sortOrder={sortOrder} column="time_to_close" /></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedLeaderboard.map((user, index) => (
                  <tr 
                    key={user.user_id}
                    className={`${
                      user.user_id === currentUserId 
                        ? isDark ? 'bg-[#A2FF00]/10 border-l-4 border-[#A2FF00]' : 'bg-green-50 border-l-4 border-green-500'
                        : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                    } transition-colors`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center w-8">
                        <RankBadge index={index} isDark={isDark} />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} flex items-center justify-center font-bold text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name}</span>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{user.leads}</td>
                    <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{user.tasks}</td>
                    <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{user.proposals}</td>
                    <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{user.won}</td>
                    <td className={`px-4 py-3 text-right font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>£{user.revenue.toLocaleString()}</td>
                    <td className={`px-4 py-3 text-right font-medium ${
                      user.close_rate >= 65 ? (isDark ? 'text-green-400' : 'text-green-600') : user.close_rate >= 50 ? (isDark ? 'text-yellow-400' : 'text-yellow-600') : (isDark ? 'text-red-400' : 'text-red-600')
                    }`}>{user.close_rate}%</td>
                    <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>£{user.avg_deal.toLocaleString()}</td>
                    <td className={`px-4 py-3 text-right font-medium ${
                      user.time_to_close <= 10 ? (isDark ? 'text-green-400' : 'text-green-600') : user.time_to_close <= 15 ? (isDark ? 'text-yellow-400' : 'text-yellow-600') : (isDark ? 'text-red-400' : 'text-red-600')
                    }`}>{user.time_to_close}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 2: Personal Performance Cards */}
        <div>
          <h2 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
            <Target className="w-5 h-5 text-[#A2FF00]" />
            Your Performance vs Team Average
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <StatCard isDark={isDark} title="Leads Contacted" value={currentUserStats?.leads || 0} icon={Users} comparison={calculatedTeamAverages.leads ? Math.round(((currentUserStats?.leads || 0) / calculatedTeamAverages.leads - 1) * 100) : 0} />
            <StatCard isDark={isDark} title="Tasks Completed" value={currentUserStats?.tasks || 0} icon={CheckCircle} comparison={calculatedTeamAverages.tasks ? Math.round(((currentUserStats?.tasks || 0) / calculatedTeamAverages.tasks - 1) * 100) : 0} />
            <StatCard isDark={isDark} title="Proposals Sent" value={currentUserStats?.proposals || 0} icon={FileText} comparison={calculatedTeamAverages.proposals ? Math.round(((currentUserStats?.proposals || 0) / calculatedTeamAverages.proposals - 1) * 100) : 0} />
            <StatCard isDark={isDark} title="Deals Won" value={currentUserStats?.won || 0} icon={Award} comparison={calculatedTeamAverages.won ? Math.round(((currentUserStats?.won || 0) / calculatedTeamAverages.won - 1) * 100) : 0} />
            <StatCard isDark={isDark} title="Revenue Won" value={currentUserStats?.revenue || 0} icon={DollarSign} prefix="£" comparison={calculatedTeamAverages.revenue ? Math.round(((currentUserStats?.revenue || 0) / calculatedTeamAverages.revenue - 1) * 100) : 0} />
            <StatCard isDark={isDark} title="Close Rate" value={currentUserStats?.close_rate || 0} icon={TrendingUp} suffix="%" comparison={calculatedTeamAverages.close_rate ? Math.round(((currentUserStats?.close_rate || 0) / calculatedTeamAverages.close_rate - 1) * 100) : 0} />
            <StatCard isDark={isDark} title="Avg Deal Size" value={currentUserStats?.avg_deal || 0} icon={BarChart3} prefix="£" comparison={calculatedTeamAverages.avg_deal ? Math.round(((currentUserStats?.avg_deal || 0) / calculatedTeamAverages.avg_deal - 1) * 100) : 0} />
            <StatCard isDark={isDark} title="Avg Days to Close" value={currentUserStats?.time_to_close || 0} icon={Clock} suffix="d" comparison={calculatedTeamAverages.time_to_close ? -Math.round(((currentUserStats?.time_to_close || 0) / calculatedTeamAverages.time_to_close - 1) * 100) : 0} />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pipeline Value</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mt-1`}>£{pipelineValue.toLocaleString()}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>{myStats?.pending_proposals || 0} proposals pending</p>
                </div>
                <div className={`w-12 h-12 rounded-full ${isDark ? 'bg-[#A2FF00]/20' : 'bg-green-100'} flex items-center justify-center`}>
                  <DollarSign className={`w-6 h-6 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                </div>
              </div>
            </div>
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Lead → Won Conversion</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mt-1`}>{conversionRate}%</p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>{myStats?.leads || 0} leads → {myStats?.won || 0} won</p>
                </div>
                <div className={`w-12 h-12 rounded-full ${isDark ? 'bg-[#A2FF00]/20' : 'bg-green-100'} flex items-center justify-center`}>
                  <TrendingUp className={`w-6 h-6 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Trend Charts */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
              <BarChart3 className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              Revenue Trend
            </h3>
            <SimpleTrendChart data={trendData.revenue || []} labels={trendData.labels || []} isDark={isDark} />
            <div className="flex justify-between mt-4 text-xs">
              <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>
                {timePeriod === 'week' ? '7 Day' : timePeriod === 'year' ? 'Quarterly' : '6 Month'} History
              </span>
              <span className={`font-medium ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                £{(trendData.revenue?.reduce((a, b) => a + b, 0) || 0).toLocaleString()} Total
              </span>
            </div>
          </div>
          
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
              <BarChart3 className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              Proposals Won Trend
            </h3>
            <SimpleTrendChart data={trendData.won || []} labels={trendData.labels || []} isDark={isDark} />
            <div className="flex justify-between mt-4 text-xs">
              <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>
                {timePeriod === 'week' ? '7 Day' : timePeriod === 'year' ? 'Quarterly' : '6 Month'} History
              </span>
              <span className={`font-medium ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                {(trendData.won?.reduce((a, b) => a + b, 0) || 0)} Won
              </span>
            </div>
          </div>
        </div>

        {/* Section 4: Analysis Charts */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
              <PieChart className="w-4 h-4 text-red-400" />
              Loss Root Causes
            </h3>
            {lossReasons.length > 0 ? (
              <>
                <DonutChart data={lossReasons} isDark={isDark} />
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'} mt-4`}>
                  Based on {lossReasons.reduce((sum, r) => sum + r.count, 0)} declined proposals this period
                </p>
              </>
            ) : (
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'} py-8 text-center`}>
                No declined proposals in this period
              </p>
            )}
          </div>
          
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
              <BarChart3 className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              Won Deals by Industry
            </h3>
            {industries.length > 0 ? (
              <>
                <SimpleBarChart 
                  isDark={isDark}
                  data={industries.map(i => ({ 
                    label: i.industry, 
                    value: i.revenue,
                    displayValue: `£${(i.revenue / 1000).toFixed(0)}k`
                  }))}
                  maxValue={Math.max(...industries.map(i => i.revenue), 1)}
                />
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'} mt-4`}>
                  Total: £{industries.reduce((sum, i) => sum + i.revenue, 0).toLocaleString()} across {industries.reduce((sum, i) => sum + i.count, 0)} deals
                </p>
              </>
            ) : (
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'} py-8 text-center`}>
                No industry data available for this period
              </p>
            )}
          </div>
        </div>

        {/* Section 5: Product Analytics */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm overflow-hidden`}>
          <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center gap-2`}>
            <Package className="w-5 h-5 text-[#A2FF00]" />
            <h2 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Product Analytics</h2>
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'} ml-2`}>Performance by product/service</span>
          </div>
          
          {/* Product Performance Table */}
          <div className="overflow-x-auto">
            {productData.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} text-xs`}>
                    <th className={`px-4 py-3 text-left ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Product / Service</th>
                    <th className={`px-4 py-3 text-right ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Revenue</th>
                    <th className={`px-4 py-3 text-right ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Deals Won</th>
                    <th className={`px-4 py-3 text-right ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Win Rate</th>
                    <th className={`px-4 py-3 text-right ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Avg Deal Size</th>
                    <th className={`px-4 py-3 text-right ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Days to Close</th>
                  </tr>
                </thead>
                <tbody>
                  {productData.map((product) => (
                    <tr 
                      key={product.product_id}
                      className={`${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: product.color || '#A2FF00' }}
                          />
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.name}</span>
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                        £{product.revenue.toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {product.deals_won}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${
                        (product.win_rate || 0) >= 70 
                          ? (isDark ? 'text-green-400' : 'text-green-600') 
                          : (product.win_rate || 0) >= 50 
                            ? (isDark ? 'text-yellow-400' : 'text-yellow-600') 
                            : (isDark ? 'text-red-400' : 'text-red-600')
                      }`}>
                        {Math.round(product.win_rate || 0)}%
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        £{(product.avg_deal || 0).toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${
                        (product.days_to_close || 0) <= 10 
                          ? (isDark ? 'text-green-400' : 'text-green-600') 
                          : (product.days_to_close || 0) <= 20 
                            ? (isDark ? 'text-yellow-400' : 'text-yellow-600') 
                            : (isDark ? 'text-red-400' : 'text-red-600')
                      }`}>
                        {product.days_to_close || 0}d
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} font-bold`}>
                    <td className={`px-4 py-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Total</td>
                    <td className={`px-4 py-3 text-right ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                      £{productData.reduce((sum, p) => sum + (p.revenue || 0), 0).toLocaleString()}
                    </td>
                    <td className={`px-4 py-3 text-right ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {productData.reduce((sum, p) => sum + (p.deals_won || 0), 0)}
                    </td>
                    <td className={`px-4 py-3 text-right ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {productData.length > 0 ? Math.round(productData.reduce((sum, p) => sum + (p.win_rate || 0), 0) / productData.length) : 0}%
                    </td>
                    <td className={`px-4 py-3 text-right ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      £{productData.reduce((sum, p) => sum + (p.deals_won || 0), 0) > 0 
                        ? Math.round(productData.reduce((sum, p) => sum + (p.revenue || 0), 0) / productData.reduce((sum, p) => sum + (p.deals_won || 0), 0)).toLocaleString() 
                        : 0}
                    </td>
                    <td className={`px-4 py-3 text-right ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {productData.reduce((sum, p) => sum + (p.deals_won || 0), 0) > 0 
                        ? Math.round(productData.reduce((sum, p) => sum + (p.days_to_close || 0) * (p.deals_won || 0), 0) / productData.reduce((sum, p) => sum + (p.deals_won || 0), 0)) 
                        : 0}d
                    </td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'} py-8 text-center`}>
                No product data available for this period
              </p>
            )}
          </div>
        </div>

        {/* Section 6: Product Analytics Charts */}
        {productData.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Revenue by Product */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
              <DollarSign className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              Revenue by Product
            </h3>
            <div className="space-y-3">
              {[...productData].sort((a, b) => b.revenue - a.revenue).slice(0, 5).map((product) => (
                <div key={product.product_id} className="flex items-center gap-3">
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-700'} w-32 truncate`}>{product.name}</span>
                  <div className={`flex-1 h-6 ${isDark ? 'bg-white/5' : 'bg-gray-200'} rounded-sm overflow-hidden`}>
                    <div 
                      className="h-full rounded-sm transition-all duration-500"
                      style={{ 
                        width: `${(product.revenue / Math.max(...productData.map(p => p.revenue), 1)) * 100}%`,
                        backgroundColor: product.color || '#A2FF00'
                      }}
                    />
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'} w-20 text-right`}>
                    £{(product.revenue / 1000).toFixed(0)}k
                  </span>
                </div>
              ))}
            </div>
            <div className={`flex justify-between mt-4 text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              <span>Top performer: {productData[0]?.name || 'N/A'}</span>
              <span className={`font-medium ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                {productData.reduce((sum, p) => sum + p.revenue, 0) > 0 
                  ? `${Math.round((productData[0]?.revenue || 0) / productData.reduce((sum, p) => sum + p.revenue, 0) * 100)}% of total`
                  : '0%'}
              </span>
            </div>
          </div>

          {/* Average Deal Size by Product */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
              <BarChart3 className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              Average Deal Size by Product
            </h3>
            <div className="space-y-3">
              {[...productData].sort((a, b) => (b.avg_deal || 0) - (a.avg_deal || 0)).slice(0, 5).map((product) => (
                <div key={product.product_id} className="flex items-center gap-3">
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-700'} w-32 truncate`}>{product.name}</span>
                  <div className={`flex-1 h-6 ${isDark ? 'bg-white/5' : 'bg-gray-200'} rounded-sm overflow-hidden`}>
                    <div 
                      className="h-full rounded-sm transition-all duration-500"
                      style={{ 
                        width: `${((product.avg_deal || 0) / Math.max(...productData.map(p => p.avg_deal || 0), 1)) * 100}%`,
                        backgroundColor: product.color || '#A2FF00'
                      }}
                    />
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'} w-20 text-right`}>
                    £{(product.avg_deal || 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
            <div className={`flex justify-between mt-4 text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              <span>Highest value: {[...productData].sort((a, b) => (b.avg_deal || 0) - (a.avg_deal || 0))[0]?.name || 'N/A'}</span>
              <span className={`font-medium ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                £{([...productData].sort((a, b) => (b.avg_deal || 0) - (a.avg_deal || 0))[0]?.avg_deal || 0).toLocaleString()} avg
              </span>
            </div>
          </div>
        </div>
        )}

        {/* Section 7: More Product Insights */}
        {productData.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Lead Time to Close by Product */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
              <Clock className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              Lead Time to Close by Product
            </h3>
            <div className="space-y-3">
              {[...productData].sort((a, b) => (a.days_to_close || 0) - (b.days_to_close || 0)).slice(0, 5).map((product) => (
                <div key={product.product_id} className="flex items-center gap-3">
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-700'} w-32 truncate`}>{product.name}</span>
                  <div className={`flex-1 h-6 ${isDark ? 'bg-white/5' : 'bg-gray-200'} rounded-sm overflow-hidden`}>
                    <div 
                      className="h-full rounded-sm transition-all duration-500"
                      style={{ 
                        width: `${((product.days_to_close || 0) / Math.max(...productData.map(p => p.days_to_close || 0), 1)) * 100}%`,
                        backgroundColor: (product.days_to_close || 0) <= 10 
                          ? '#22c55e' 
                          : (product.days_to_close || 0) <= 20 
                            ? '#eab308' 
                            : '#ef4444'
                      }}
                    />
                  </div>
                  <span className={`text-xs font-medium w-16 text-right ${
                    (product.days_to_close || 0) <= 10 
                      ? (isDark ? 'text-green-400' : 'text-green-600') 
                      : (product.days_to_close || 0) <= 20 
                        ? (isDark ? 'text-yellow-400' : 'text-yellow-600') 
                        : (isDark ? 'text-red-400' : 'text-red-600')
                  }`}>
                    {product.days_to_close || 0} days
                  </span>
                </div>
              ))}
            </div>
            <div className={`flex justify-between mt-4 text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              <span>Fastest: {[...productData].sort((a, b) => (a.days_to_close || 0) - (b.days_to_close || 0))[0]?.name || 'N/A'}</span>
              <span className={`font-medium ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                Avg: {productData.length > 0 ? Math.round(productData.reduce((sum, p) => sum + (p.days_to_close || 0), 0) / productData.length) : 0} days
              </span>
            </div>
          </div>

          {/* Top Bundled Products */}
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
            <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
              <Layers className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              Top Product Bundles
            </h3>
            {bundledProducts.length > 0 ? (
              <>
                <div className="space-y-3">
                  {bundledProducts.map((bundle, index) => (
                    <div key={index} className={`p-3 rounded-sm ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{bundle.bundle}</p>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'} mt-1`}>
                            Sold {bundle.count} times
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                            £{bundle.revenue.toLocaleString()}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                            £{bundle.count > 0 ? Math.round(bundle.revenue / bundle.count).toLocaleString() : 0} avg
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={`flex justify-between mt-4 text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                  <span>Total bundle revenue</span>
                  <span className={`font-medium ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                    £{bundledProducts.reduce((sum, b) => sum + b.revenue, 0).toLocaleString()}
                  </span>
                </div>
              </>
            ) : (
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'} py-8 text-center`}>
                No bundle data available for this period
              </p>
            )}
          </div>
        </div>
        )}

        {/* Section 8: Industry Summary */}
        {industries.length > 0 && (
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-300 shadow-sm'} border rounded-sm p-4`}>
          <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
            <Target className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
            Industry Overview
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {industries.slice(0, 6).map((item, index) => (
              <div 
                key={index} 
                className={`p-3 rounded-sm ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: ['#A2FF00', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899', '#10B981'][index % 6] }}
                  />
                  <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.industry}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.count} deals</span>
                  <span className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                    £{(item.revenue / 1000).toFixed(0)}k
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'} mt-4`}>
            Top industry: {industries[0]?.industry || 'N/A'} (£{((industries[0]?.revenue || 0) / 1000).toFixed(0)}k revenue)
          </p>
        </div>
        )}
      </div>
    </AppLayout>
  );
};

export default IntelligencePage;
